import {
  ChangeDetectionStrategy, Component, computed, forwardRef, inject, input,
  LOCALE_ID, output, signal
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectChangeEvent, SelectModule } from 'primeng/select';
import { formatUnitNumber } from '../../helpers/unit-number-format.helper';
import {
  DEFAULT_UNIT_PRECISION_MAP, UnitPrecisionMap, UnitPrecisionOverrides
} from '../../helpers/unit-precision.helper';

@Component({
  selector: 'app-units-select-input',
  standalone: true,
  imports: [FormsModule, SelectModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block min-w-0' },
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => UnitsSelectInputComponent),
    multi: true
  }],
  template: `
    <p-select
      class="w-full"
      [options]="selectOptions()"
      [optionLabel]="optionLabel()"
      [optionValue]="optionValue()"
      [dataKey]="dataKey()"
      [ngModel]="value()"
      [ngModelOptions]="{ standalone: true }"
      [disabled]="readOnly() || formDisabled()"
      [placeholder]="placeholder()"
      [appendTo]="appendTo()"
      [filter]="filter()"
      [filterBy]="filterBy()"
      [inputId]="inputId()"
      [ariaLabel]="ariaLabel()"
      (onChange)="selectValue($event)"
      (onBlur)="markTouched()">
      <ng-template #item let-option>{{ formatOption(option) }}</ng-template>
      <ng-template #selectedItem>
        @if (selectedOption(); as option) {
          {{ formatOption(option) }}
        } @else {
          {{ placeholder() }}
        }
      </ng-template>
    </p-select>
  `
})
export class UnitsSelectInputComponent<T extends object> implements ControlValueAccessor {
  readonly options = input<readonly T[]>([]);
  readonly optionLabel = input<(keyof T & string) | undefined>(undefined);
  readonly optionLabelFormatter = input<((option: T) => string) | undefined>(undefined);
  readonly optionValue = input<(keyof T & string) | undefined>(undefined);
  readonly dataKey = input<(keyof T & string) | undefined>(undefined);
  readonly unit = input<string>('');
  readonly unitPrecisionMap = input<UnitPrecisionMap>(DEFAULT_UNIT_PRECISION_MAP);
  readonly unitPrecisionOverrides = input<UnitPrecisionOverrides>({});
  readonly readOnly = input(false);
  readonly placeholder = input('Select');
  readonly appendTo = input('body');
  readonly filter = input(false);
  readonly filterBy = input<(keyof T & string) | undefined>(undefined);
  readonly inputId = input<string | undefined>(undefined);
  readonly ariaLabel = input<string | undefined>(undefined);
  readonly onChange = output<SelectChangeEvent>();

  protected readonly value = signal<unknown>(null);
  protected readonly formDisabled = signal(false);
  protected readonly selectOptions = computed(() => [...this.options()]);
  protected readonly selectedOption = computed(() => {
    const optionValue = this.optionValue();
    const dataKey = this.dataKey();
    const value = this.value() as Record<string, unknown> | null;
    return this.options().find(option => {
      if (optionValue) {
        return option[optionValue] === value;
      }
      return dataKey && value != null ? option[dataKey] === value[dataKey] : option === value;
    });
  });
  private readonly locale = inject(LOCALE_ID);
  private change: (value: unknown) => void = () => {};
  private touched: () => void = () => {};

  public formatOption(option: T | null | undefined): string {
    if (option == null) {
      return '';
    }
    const formatter = this.optionLabelFormatter();
    if (formatter) {
      return formatter(option);
    }
    const optionLabel = this.optionLabel();
    const number = optionLabel ? option[optionLabel] : undefined;
    if (number == null) {
      return '';
    }
    if (typeof number !== 'number') {
      throw new TypeError('Units select option labels must be numeric.');
    }
    return formatUnitNumber(number, this.unit(), this.locale, this.unitPrecisionMap(), this.unitPrecisionOverrides());
  }

  public writeValue(value: unknown): void {
    this.value.set(value);
  }

  public registerOnChange(fn: (value: unknown) => void): void {
    this.change = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.touched = fn;
  }

  public setDisabledState(disabled: boolean): void {
    this.formDisabled.set(disabled);
  }

  protected selectValue(event: SelectChangeEvent): void {
    this.value.set(event.value);
    this.change(event.value);
    this.onChange.emit(event);
  }

  protected markTouched(): void {
    this.touched();
  }
}
